import React, { memo } from 'react';
import styled from 'styled-components';

import styles from '@front/common/src/components/icon-button/styles';
import { IconButtonCommonProps, StyledIconButtonProps } from '@front/common/src/components/icon-button/types';
import colors from '@front/common/src/utils/colors';

import { getGradient } from '../../styles/gradient';
import Icon from '../icon/icon';
import { StylelessButton } from '../utils';
import { boxShadow } from '../../styles/mixins';
import { WithClassNameProps } from '../types';

const disabledStyle = `
  ${styles.disabled}
  cursor: not-allowed;
`;

const Button = styled(StylelessButton)<StyledIconButtonProps>`
  background: ${({ $backgroundColor }) => $backgroundColor};

  &:hover {
    ${({ $isDisabled, $isGradientBackgroundColor }) =>
      !$isDisabled &&
      `
        background: ${$isGradientBackgroundColor ? getGradient('gradient110') : colors.ink10};
      `}
  }

  &:active {
    ${({ $isDisabled, $isGradientBackgroundColor }) =>
      !$isDisabled &&
      `
        background: ${$isGradientBackgroundColor ? getGradient('gradient120') : colors.ink20};
      `}
  }
  ${styles.button}
  ${boxShadow.normal}
  ${({ $isDisabled }) => $isDisabled && disabledStyle}
`;

const StyledIcon = styled(Icon)`
  position: absolute;
`;

export type IconButtonProps = IconButtonCommonProps &
  WithClassNameProps & {
    onPress: React.MouseEventHandler<HTMLButtonElement>;
  };

const IconButton = ({
  className,
  iconName,
  iconSize = 10,
  isDisabled = false,
  label,
  onPress,
  size = 16,
  type = 'primary',
}: IconButtonProps) => {
  const { backgroundColor, borderColor, iconColor } = styles.colors[type];
  const isGradientBackgroundColor = backgroundColor === 'gradient';

  return (
    <Button
      $backgroundColor={isGradientBackgroundColor ? getGradient() : backgroundColor}
      $isGradientBackgroundColor={isGradientBackgroundColor}
      $shouldHideBorderWidth={isGradientBackgroundColor}
      $borderColor={borderColor}
      $isDisabled={isDisabled}
      $size={size}
      aria-label={label}
      className={className}
      disabled={isDisabled}
      onClick={onPress}
      title={label}
      type='button'
    >
      <StyledIcon name={iconName} color={iconColor} size={iconSize} />
    </Button>
  );
};

export default memo(IconButton);
